import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './hooks';
import { setCredentials } from './store/slices/authSlice';
import { authApi } from './services/api';
import { Token } from './types';
import tokenService from './services/tokenService';

import Layout from './components/layout/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import Toast from './components/common/Toast';

// Import pages directly to ensure they're loaded
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Organizations from './pages/Organizations';
import Settings from './pages/Settings';
import AuditLogs from './pages/AuditLogs';
import Billing from './pages/Billing';
import Limits from './pages/Limits';
import UsageStats from './pages/UsageStats';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Protected Route Component for Super Admin only
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAppSelector((state) => state.auth);
  return user?.role === 'super_admin' ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

// Protected Route Component for Super Admin and Org Admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAppSelector((state) => state.auth);
  return (user?.role === 'super_admin' || user?.role === 'org_admin') ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const { message, type, isVisible } = useAppSelector((state) => state.toast);
  const [appLoading, setAppLoading] = React.useState(true);

  // Don't show global loading spinner on auth pages that handle their own loading states
  const authPages = ['/login', '/forgot-password', '/reset-password'];
  const shouldShowGlobalLoading = !authPages.includes(location.pathname);

  // Initialize theme based on system preference
  useEffect(() => {
    // Check if theme is stored in localStorage
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      document.documentElement.classList.add(storedTheme);
      return;
    }

    // If no stored theme, use system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.add('light');
      localStorage.setItem('theme', 'light');
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('theme')) return; // Don't override manual preference
      if (e.matches) {
        document.documentElement.classList.remove('light');
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedRefreshToken) {
        try {
          const tokenObj: Token = {
            access_token: storedToken,
            token_type: 'bearer',
            expires_in: 3600,
            refresh_token: storedRefreshToken
          };

          let userData;
          // If we have stored user data, use it immediately
          if (storedUser) {
            userData = JSON.parse(storedUser);
          } else {
            // Only call /me if we don't have stored user data
            const response = await authApi.getCurrentUser();
            userData = response.data;
            localStorage.setItem('user', JSON.stringify(userData));
          }

          // Check if user role is allowed to access the admin dashboard
          const allowedRoles = ['super_admin', 'org_admin'];
          if (!allowedRoles.includes(userData.role)) {
            // Clear all stored data for unauthorized users
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            console.log('Access denied: User role not authorized for admin dashboard');
            setAppLoading(false);
            return;
          }

          dispatch(setCredentials({
            user: userData,
            token: tokenObj,
            refresh_token: storedRefreshToken
          }));
        } catch (error) {
          console.error('Failed to restore session:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }
      }
      setAppLoading(false);
    };

    initializeAuth();
  }, [dispatch]);

  // Start token monitoring when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      tokenService.startTokenMonitoring();
      
      // Listen for token events
      const handleTokenRefreshed = () => {
        console.log('Token refreshed successfully');
      };
      
      const handleTokenExpired = () => {
        console.log('Token expired, user will be redirected to login');
        // Clear Redux state
        dispatch({ type: 'auth/logout' });
      };
      
      window.addEventListener('tokenRefreshed', handleTokenRefreshed);
      window.addEventListener('tokenExpired', handleTokenExpired);
      
      return () => {
        window.removeEventListener('tokenRefreshed', handleTokenRefreshed);
        window.removeEventListener('tokenExpired', handleTokenExpired);
      };
    } else {
      // Stop monitoring when user is not authenticated
      tokenService.stopTokenMonitoring();
    }
  }, [isAuthenticated, dispatch]);

  // Cleanup token monitoring on unmount
  useEffect(() => {
    return () => {
      tokenService.stopTokenMonitoring();
    };
  }, []);

  if (appLoading || (isLoading && shouldShowGlobalLoading)) {
    return <LoadingSpinner />;
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white dark:bg-black">
      {isVisible && (
        <Toast
          message={message || ''}
          type={type}
          onClose={() => dispatch({ type: 'toast/hideToast' })}
        />
      )}
      <div className="h-8 bg-light-surface dark:bg-dark-surface shadow-sm z-[60] border-b border-light-border dark:border-[#ffffff26]">
        <div className="h-full flex items-center justify-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Built with ❤️ by <span className="text-[#FF3D00]">Ana</span>
          </p>
        </div>
      </div>

      <div className="flex-1">
        <Routes>
          <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/" />} />
          <Route path="/reset-password" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/" />} />
          
          <Route element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}>
            <Route index element={<Navigate to="/organizations" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="organizations" element={<Organizations />} />
            <Route path="settings" element={<Settings />} />
            <Route path="audit-logs" element={
              <AdminRoute>
                <AuditLogs />
              </AdminRoute>
            } />
            <Route path="billing" element={
              <SuperAdminRoute>
                <Billing />
              </SuperAdminRoute>
            } />
            <Route path="limits" element={
              <SuperAdminRoute>
                <Limits />
              </SuperAdminRoute>
            } />
            <Route path="usage-stats" element={
              <AdminRoute>
                <UsageStats />
              </AdminRoute>
            } />
          </Route>
        </Routes>
      </div>
    </div>
  );
};

export default App;
